import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useNav } from '../nav/NavContext.js'

export type FieldType = 'text' | 'number' | 'select' | 'checkbox' | 'date'

export interface SelectOption {
  value: string
  label: string
}

export interface MasterField {
  name: string
  label: string
  type: FieldType
  options?: SelectOption[]
  /** For a select whose choices come from another master (e.g. vehicle type),
   * resolved once when the form opens rather than hardcoded. */
  loadOptions?: () => Promise<SelectOption[]>
  required?: boolean
  placeholder?: string
  /** Shown as help text under the input — e.g. GSTIN format. */
  hint?: string
  /**
   * Initial value for the Add form. Required whenever the backend's own
   * column default is anything other than "" / false — e.g.
   * customers.requires_quotation_acceptance defaults to true in the schema,
   * so a checkbox silently defaulting to unchecked would submit the opposite
   * of the intended default on every new record unless the user happens to
   * notice and correct it.
   */
  defaultValue?: unknown
}

export interface MasterColumn {
  key: string
  label: string
  render?: (row: Record<string, unknown>) => string
}

export interface MasterScreenConfig {
  title: string
  /** Path under /api, e.g. "/customers". */
  apiPath: string
  columns: MasterColumn[]
  fields: MasterField[]
  searchPlaceholder?: string
}

interface ListResponse {
  rows: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

function emptyForm(fields: MasterField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of fields) {
    out[f.name] = f.defaultValue !== undefined ? f.defaultValue : f.type === 'checkbox' ? false : ''
  }
  return out
}

/**
 * Stage 3 §5: "All masters below share a common screen pattern" — List
 * (searchable, filterable, paginated) → Add/Edit form → Deactivate (soft
 * delete). One component, configured per master, rather than eleven
 * near-identical screens — the backend already made the same call with
 * createMasterRouter (apps/api/src/lib/masterRouter.ts).
 */
export function MasterScreen({ config }: { config: MasterScreenConfig }) {
  const { can } = useAuth()
  const { go } = useNav()
  // '/vehicle-types' -> vehicle_types: the audit trail is keyed by table name.
  const auditTable = config.apiPath.slice(1).replace(/-/g, '_')
  const [data, setData] = useState<ListResponse | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, unknown>>(() => emptyForm(config.fields))
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingOverride, setPendingOverride] = useState<{ message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, SelectOption[]>>({})

  useEffect(() => {
    for (const field of config.fields) {
      if (field.loadOptions) {
        field
          .loadOptions()
          .then((opts) => setDynamicOptions((prev) => ({ ...prev, [field.name]: opts })))
          .catch(() => {})
      }
    }
    // Deliberately once per mount, not per config.fields identity: each master
    // tab remounts MasterScreen via a `key` prop (see MastersPage), so a fresh
    // mount is exactly when options should be (re)loaded.
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setListError(null)
    try {
      const query = new URLSearchParams({ page: String(page), status: 'active' })
      if (search.trim()) query.set('search', search.trim())
      const result = await api.get<ListResponse>(`${config.apiPath}?${query.toString()}`)
      setData(result)
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [config.apiPath, page, search])

  useEffect(() => {
    void load()
  }, [load])

  function openCreate() {
    setEditingId(null)
    setForm(emptyForm(config.fields))
    setFormError(null)
    setPendingOverride(null)
    setFormOpen(true)
  }

  function openEdit(row: Record<string, unknown>) {
    setEditingId(String(row.id))
    const next: Record<string, unknown> = {}
    for (const f of config.fields) next[f.name] = row[f.name] ?? (f.type === 'checkbox' ? false : '')
    setForm(next)
    setFormError(null)
    setPendingOverride(null)
    setFormOpen(true)
  }

  function updateField(name: string, value: unknown) {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  function fieldInputProps(field: MasterField) {
    return {
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const el = e.target
        const value =
          field.type === 'checkbox' && el instanceof HTMLInputElement
            ? el.checked
            : field.type === 'number'
              ? el.value === ''
                ? ''
                : Number(el.value)
              : el.value
        updateField(field.name, value)
      },
    }
  }

  async function submit(e: FormEvent, confirm = false) {
    e.preventDefault()
    setSaving(true)
    setFormError(null)
    try {
      // Strip empty-string optionals so an untouched field doesn't get sent as
      // "" and fail a format check meant for "not provided at all".
      const body: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(form)) {
        if (v !== '') body[k] = v
      }
      const suffix = confirm ? '?confirm=true' : ''
      if (editingId) {
        await api.put(`${config.apiPath}/${editingId}${suffix}`, body)
      } else {
        await api.post(`${config.apiPath}${suffix}`, body)
      }
      setFormOpen(false)
      setPendingOverride(null)
      await load()
    } catch (err) {
      if (err instanceof ApiError && err.requiresOverride) {
        // Stage 4/6 pattern: a soft rule tripped. Offer to proceed rather than
        // just reporting failure — the action is allowed, just not silent.
        setPendingOverride({ message: err.message })
      } else {
        setFormError(err instanceof ApiError ? err.message : 'Save failed')
      }
    } finally {
      setSaving(false)
    }
  }

  async function deactivate(row: Record<string, unknown>) {
    if (!window.confirm(`Deactivate this record? It can still be seen in historical data.`)) return
    try {
      await api.patch(`${config.apiPath}/${row.id}/deactivate`)
      await load()
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Deactivate failed')
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <div>
      <div className="tf-toolbar">
        <input
          className="tf-search"
          placeholder={config.searchPlaceholder ?? 'Search…'}
          value={search}
          onChange={(e) => {
            setPage(1)
            setSearch(e.target.value)
          }}
        />
        <button type="button" className="tf-button-primary" onClick={openCreate}>
          + Add
        </button>
      </div>

      {listError && <p className="tf-error">{listError}</p>}

      <table className="tf-table">
        <thead>
          <tr>
            {config.columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={config.columns.length + 1}>Loading…</td>
            </tr>
          )}
          {!loading && data?.rows.length === 0 && (
            <tr>
              <td colSpan={config.columns.length + 1}>No records found</td>
            </tr>
          )}
          {!loading &&
            data?.rows.map((row) => (
              <tr key={String(row.id)}>
                {config.columns.map((c) => (
                  <td key={c.key}>{c.render ? c.render(row) : String(row[c.key] ?? '')}</td>
                ))}
                <td className="tf-row-actions">
                  <button type="button" onClick={() => openEdit(row)}>
                    Edit
                  </button>
                  <button type="button" onClick={() => void deactivate(row)}>
                    Deactivate
                  </button>
                  {can('audit_log.read') && (
                    <button type="button" onClick={() => go('audit', { table: auditTable, record: String(row.id) })}>
                      History
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {data && data.total > data.pageSize && (
        <div className="tf-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {formOpen && (
        <div className="tf-slideover-backdrop" onClick={() => setFormOpen(false)}>
          <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
            <h2>
              {editingId ? 'Edit' : 'Add'} {config.title}
            </h2>
            <form onSubmit={(e) => void submit(e)} className="tf-form">
              {config.fields.map((field) => (
                <label className="tf-field" key={field.name}>
                  <span>{field.label}</span>
                  {field.type === 'select' ? (
                    <select
                      value={String(form[field.name] ?? '')}
                      required={field.required}
                      {...fieldInputProps(field)}
                    >
                      <option value="" disabled>
                        Select…
                      </option>
                      {(dynamicOptions[field.name] ?? field.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <input type="checkbox" checked={Boolean(form[field.name])} {...fieldInputProps(field)} />
                  ) : (
                    <input
                      type={field.type}
                      value={String(form[field.name] ?? '')}
                      placeholder={field.placeholder}
                      required={field.required}
                      {...fieldInputProps(field)}
                    />
                  )}
                  {field.hint && <small>{field.hint}</small>}
                </label>
              ))}

              {formError && <p className="tf-error">{formError}</p>}

              {pendingOverride && (
                <div className="tf-override-banner">
                  <p>{pendingOverride.message}</p>
                  <button
                    type="button"
                    className="tf-button-primary"
                    disabled={saving}
                    onClick={(e) => void submit(e, true)}
                  >
                    {saving ? 'Saving…' : 'Override & Confirm'}
                  </button>
                </div>
              )}

              <div className="tf-form-actions">
                <button type="submit" className="tf-button-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => setFormOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
